// Utility functions for chunked uploads
import { videoAPI } from './api';

// Chunk size configuration from environment variables
export const INITIAL_CHUNK_SIZE = parseInt(process.env.REACT_APP_CHUNK_SIZE) || 25 * 1024 * 1024; // 25MB initial chunks
export const MIN_CHUNK_SIZE = 1 * 1024 * 1024; // 1MB minimum chunk size
export const MAX_CHUNK_SIZE = 50 * 1024 * 1024; // 50MB maximum chunk size

// Adaptive chunk sizing variables
let currentChunkSize = INITIAL_CHUNK_SIZE;
let uploadSpeedHistory = []; // Store recent upload speeds in bytes per second
const SPEED_HISTORY_SIZE = 5; // Track last 5 measurements

// Function to update the chunk size based on upload speed
export const adjustChunkSize = (uploadSpeed) => {
  // Add current upload speed to history
  uploadSpeedHistory.push(uploadSpeed);
  if (uploadSpeedHistory.length > SPEED_HISTORY_SIZE) {
    uploadSpeedHistory.shift(); // Remove oldest measurement
  }

  // Calculate average upload speed from history
  const avgUploadSpeed = uploadSpeedHistory.reduce((sum, speed) => sum + speed, 0) / uploadSpeedHistory.length;

  // Adjust chunk size based on average upload speed
  // If upload is fast, we can use larger chunks; if slow, use smaller chunks
  if (avgUploadSpeed > 5 * 1024 * 1024) { // > 5 MB/s
    currentChunkSize = Math.min(MAX_CHUNK_SIZE, currentChunkSize * 1.2); // Increase chunk size
  } else if (avgUploadSpeed < 1 * 1024 * 1024) { // < 1 MB/s
    currentChunkSize = Math.max(MIN_CHUNK_SIZE, currentChunkSize * 0.8); // Decrease chunk size
  }

  return Math.round(currentChunkSize);
};

// Rate limiting variables
let lastRequestTime = 0;
const MIN_REQUEST_INTERVAL = parseInt(process.env.REACT_APP_MIN_REQUEST_INTERVAL) || 50; // 50ms between requests (reduced from 100ms for better performance)

// Helper function to implement rate limiting
const rateLimitDelay = async () => {
  const now = Date.now();
  const timeSinceLastRequest = now - lastRequestTime;

  if (timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
    const delay = MIN_REQUEST_INTERVAL - timeSinceLastRequest;
    await new Promise(resolve => setTimeout(resolve, delay));
  }

  lastRequestTime = Date.now();
};

// Helper function to decode JWT token and extract user ID
const getUserIdFromToken = (token) => {
  try {
    if (!token) return null;
    
    // Split the token and decode the payload
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    
    // Decode base64 (browser support)
    let jsonPayload;
    if (typeof atob !== 'undefined') {
      // Browser environment
      jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));
    } else {
      // Node.js environment - use Buffer
      const buff = Buffer.from(base64, 'base64');
      jsonPayload = buff.toString('utf-8');
    }
    
    const payload = JSON.parse(jsonPayload);
    return payload.userId || payload.user || payload.id;
  } catch (error) {
    console.error('Failed to decode JWT token:', error);
    return null;
  }
};

/**
 * Upload a file in chunks with resumable support
 * @param {File} file - The file to upload
 * @param {Object} metadata - Metadata for the upload session
 * @param {Function} onProgress - Progress callback function
 * @param {Function} onError - Error callback function
 * @returns {Promise<Object>} Upload result
 */
export const uploadFileInChunks = async (file, metadata, onProgress, onError) => {
  try {
    // Get user ID from JWT token
    const token = localStorage.getItem('token');
    const userId = getUserIdFromToken(token);
    
    if (!userId) {
      throw new Error('Unable to extract user ID from token');
    }
    
    // Get CSRF token
    const csrfResponse = await videoAPI.getCSRFToken(userId);
    const csrfToken = csrfResponse.data.csrfToken;
    
    // Initialize upload session
    const sessionData = {
      filename: file.name,
      originalName: file.name,
      totalSize: file.size,
      chunkCount: Math.ceil(file.size / INITIAL_CHUNK_SIZE),
      ...metadata
    };
    
    // Convert reportClasses object to array of selected class names
    if (sessionData.reportClasses && typeof sessionData.reportClasses === 'object' && !Array.isArray(sessionData.reportClasses)) {
      const selectedClasses = [];
      const classMapping = {
        passengerVehicle: 'Passenger Vehicle',
        threeAxelTruck: '3-Axel Truck',
        bicycle: 'Bicycle',
        pedestrians: 'Pedestrians',
        commercialTruck: 'Commercial Truck',
        bus: 'Bus'
      };
      
      Object.keys(sessionData.reportClasses).forEach(key => {
        if (sessionData.reportClasses[key] && classMapping[key]) {
          selectedClasses.push(classMapping[key]);
        }
      });
      
      sessionData.reportClasses = selectedClasses;
    }
    
    const sessionResponse = await videoAPI.initializeUploadSession(userId, sessionData, csrfToken);
    const sessionId = sessionResponse.data.sessionId;
        
    // Reset adaptive chunk sizing for new upload
    currentChunkSize = INITIAL_CHUNK_SIZE;
    uploadSpeedHistory = [];

    // Upload chunks
    let uploadedChunks = 0;
    let finalResponse = null;

    // Track byte position for correct chunk slicing
    let bytePosition = 0;
    let chunkIndex = 0;

    // Upload until we've sent all bytes (not just totalChunks, in case chunk size changes)
    while (bytePosition < file.size) {
      // Use adaptive chunk size (but ensure we don't exceed file size)
      const chunkSize = Math.min(currentChunkSize, file.size - bytePosition);
      const start = bytePosition;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);

      // Update byte position for next chunk
      bytePosition = end;

      // Create form data for this chunk
      const chunkFormData = new FormData();
      chunkFormData.append('chunk', chunk, `chunk_${chunkIndex}`);

      try {
        // Record the start time to calculate upload speed
        const startTime = Date.now();

        // Upload the chunk with retry logic and CSRF token
        // Pass a custom progress callback that calculates overall progress
        const chunkResponse = await uploadChunkWithRetry(
          userId,
          sessionId,
          chunkIndex,
          chunkFormData,
          (chunkProgress) => {
            // Calculate overall progress based on bytes uploaded
            const bytesUploaded = bytePosition - chunk.size + (chunk.size * (chunkProgress / 100));
            const overallProgress = Math.round((bytesUploaded / file.size) * 100);
            onProgress(overallProgress);
          },
          csrfToken
        );

        // Calculate upload speed for this chunk
        const endTime = Date.now();
        const uploadTimeSec = (endTime - startTime) / 1000; // in seconds
        const chunkSizeInBytes = chunk.size; // Use actual chunk size
        const uploadSpeed = uploadTimeSec > 0 ? chunkSizeInBytes / uploadTimeSec : 0;

        // Adjust chunk size based on upload speed (though this won't affect current upload due to server constraints)
        if (uploadTimeSec > 0) {
          adjustChunkSize(uploadSpeed);
        }

        // Check if this is the final chunk response which contains the videoId
        if (chunkResponse && chunkResponse.data && chunkResponse.data.videoId) {
          finalResponse = chunkResponse.data;
        }

        uploadedChunks++;
        chunkIndex++;

        // Update progress based on bytes uploaded
        const overallProgress = Math.round((bytePosition / file.size) * 100);
        onProgress(overallProgress);
      } catch (error) {

        // Handle rate limit errors specifically
        if (error.response && error.response.status === 429) {
          onError(`Rate limit exceeded. Please wait a few minutes and try again.`);
        } else {
          // Provide more specific error messages
          let errorMessage = error.message;
          if (error.response) {
            if (error.response.status === 400) {
              errorMessage = `Bad request for chunk ${chunkIndex}: ${error.response.data.message || 'Invalid chunk data'}`;
            } else if (error.response.status === 401) {
              errorMessage = 'Authentication failed. Please log in again.';
            } else if (error.response.status === 403) {
              errorMessage = `Access denied for chunk ${chunkIndex}. You do not have permission to upload this chunk.`;
            } else if (error.response.status === 404) {
              errorMessage = 'Upload session not found. The upload may have expired.';
            } else if (error.response.status === 500) {
              errorMessage = `Server error when uploading chunk ${chunkIndex}: ${error.response.data.message || 'Internal server error. Please try again.'}`;
            } else {
              errorMessage = `Failed to upload chunk ${chunkIndex} (${error.response.status}): ${error.response.data.message || error.message}`;
            }
          }
          onError(errorMessage);
        }
        throw error;
      }
    }
    
    // Return the final response (which contains videoId) instead of trying to get session status
    if (finalResponse) {
      return finalResponse;
    } else {
      // Fallback: try to get session status (for cases where final chunk didn't return videoId)
      try {
        const statusResponse = await videoAPI.getUploadSessionStatus(userId, sessionId);
        return statusResponse.data;
      } catch (error) {
        // If we can't get session status, return a success object
        return { message: 'Upload completed successfully' };
      }
    }
  } catch (error) {
    console.error('Chunked upload failed:', error);
    onError(`Upload failed: ${error.message}`);
    throw error;
  }
};

/**
 * Upload a single chunk with retry logic and rate limiting
 * @param {string} sessionId - Upload session ID
 * @param {number} chunkIndex - Index of the chunk
 * @param {FormData} chunkData - Form data containing the chunk
 * @param {Function} onProgress - Progress callback
 * @param {string} csrfToken - CSRF token for authentication
 * @param {number} maxRetries - Maximum number of retries (default: 5)
 * @returns {Promise<Object>} Upload result
 */
const uploadChunkWithRetry = async (userId, sessionId, chunkIndex, chunkData, onProgress, csrfToken, maxRetries = 5) => {
  let attempts = 0;

  while (attempts < maxRetries) {
    try {
      // Apply rate limiting before each request
      await rateLimitDelay();

      const result = await videoAPI.uploadChunk(userId, sessionId, chunkIndex, chunkData, onProgress, csrfToken);
      return result;
    } catch (error) {
      attempts++;
      console.warn(`Chunk ${chunkIndex} upload attempt ${attempts} failed:`, error.message);
      console.warn(`Error details:`, error);

      // Check if this is a timeout error
      const isTimeoutError = error.code === 'ECONNABORTED' || error.message.includes('timeout');

      // If we get a 429 error (rate limit), wait longer before retrying
      if (error.response && error.response.status === 429) {
        // For rate limit errors, use longer exponential backoff
        const delay = Math.pow(3, attempts) * 1000; // 3^attempts seconds
        await new Promise(resolve => setTimeout(resolve, delay));
      } else if (isTimeoutError) {
        // Handle timeout errors with longer exponential backoff
        console.warn(`Timeout error for chunk ${chunkIndex}, attempt ${attempts}. Using extended backoff.`);
        // Double the delay time for timeout errors to give more time for network recovery
        const delay = Math.pow(4, attempts) * 1000; // 4^attempts seconds for timeouts
        await new Promise(resolve => setTimeout(resolve, delay));
      } else if (error.response && error.response.status === 500) {
        // For 500 errors, provide more specific error handling
        console.error(`Server error (500) for chunk ${chunkIndex}:`, error.response.data);
        throw new Error(`Server error when uploading chunk ${chunkIndex + 1}: ${error.response.data.message || 'Internal server error'}`);
      } else if (attempts >= maxRetries) {
        // Provide more specific error messages based on the error type
        let errorMessage = error.message;
        if (error.response) {
          if (error.response.status === 400) {
            errorMessage = `Bad request: ${error.response.data.message || 'Invalid chunk data'}`;
          } else if (error.response.status === 401) {
            errorMessage = 'Authentication failed. Please log in again.';
          } else if (error.response.status === 403) {
            errorMessage = 'Access denied. You do not have permission to upload this chunk.';
          } else if (error.response.status === 404) {
            errorMessage = 'Upload session not found. The upload may have expired.';
          } else if (error.response.status >= 500) {
            errorMessage = `Server error (${error.response.status}): ${error.response.data.message || 'Internal server error'}`;
          }
        }
        throw new Error(errorMessage);
      } else {
        // Regular exponential backoff for other errors
        const delay = Math.pow(2, attempts) * 1000; // 2^attempts seconds
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
};

/**
 * Resume an interrupted upload
 * @param {string} sessionId - Existing session ID
 * @param {File} file - The file to resume uploading
 * @param {Function} onProgress - Progress callback
 * @param {Function} onError - Error callback
 * @returns {Promise<Object>} Upload result
 */
export const resumeUpload = async (sessionId, file, onProgress, onError) => {
  try {
    // Get user ID from JWT token
    const token = localStorage.getItem('token');
    const userId = getUserIdFromToken(token);
    
    if (!userId) {
      throw new Error('Unable to extract user ID from token');
    }
    
    // Get CSRF token for resuming upload
    const csrfResponse = await videoAPI.getCSRFToken(userId);
    const csrfToken = csrfResponse.data.csrfToken;
    
    // Get current session status
    const statusResponse = await videoAPI.getUploadSessionStatus(userId, sessionId);
    const sessionStatus = statusResponse.data;
        
    // Resume upload from the point where it left off
    let resumedChunks = sessionStatus.chunksReceived ? sessionStatus.chunksReceived.length : 0;
    const totalChunks = sessionStatus.chunkCount;
        
    // Convert reportClasses object to array of selected class names if needed
    const sessionData = {
      ...sessionStatus,
      reportClasses: sessionStatus.reportClasses || []
    };
    
    // Convert reportClasses object to array if it's an object
    if (sessionData.reportClasses && typeof sessionData.reportClasses === 'object' && !Array.isArray(sessionData.reportClasses)) {
      const selectedClasses = [];
      const classMapping = {
        passengerVehicle: 'Passenger Vehicle',
        commercialTruck: 'Commercial Truck',
        bus: 'Bus',
        threeAxelTruck: '3-Axel Truck',
        bicycle: 'Bicycle',
        pedestrians: 'Pedestrians'
      };
      
      Object.keys(sessionData.reportClasses).forEach(key => {
        if (sessionData.reportClasses[key] && classMapping[key]) {
          selectedClasses.push(classMapping[key]);
          // Add sub-classes if parent is selected
          if (key === 'passengerVehicle') {
            if (sessionData.reportClasses.commercialTruck) selectedClasses.push('Commercial Truck');
            if (sessionData.reportClasses.bus) selectedClasses.push('Bus');
          }
        }
      });
      
      sessionData.reportClasses = selectedClasses;
    }
    
    let finalResponse = null;

    // Calculate byte position from already uploaded chunks
    // For resume, we need to track actual bytes uploaded, not just chunk count
    let bytePosition = 0;
    let chunkIndex = 0;

    // Calculate byte position based on chunks already received
    // We need to recalculate based on actual chunk sizes since adaptive sizing may have changed
    if (sessionStatus.chunksReceived && sessionStatus.chunksReceived.length > 0) {
      // For simplicity, we'll start from the beginning byte position
      // The server will handle duplicate chunks gracefully
      // A more sophisticated approach would track exact byte positions per chunk
      const sortedChunks = [...sessionStatus.chunksReceived].sort((a, b) => a - b);
      // Estimate byte position based on average chunk size
      const avgChunkSize = sessionStatus.totalSize / sessionStatus.chunkCount;
      bytePosition = Math.min(sortedChunks.length * avgChunkSize, sessionStatus.totalSize);
      chunkIndex = sortedChunks.length;
    }

    // Upload until we've sent all bytes
    while (bytePosition < file.size) {
      // Use adaptive chunk size (but ensure we don't exceed file size)
      const chunkSize = Math.min(currentChunkSize, file.size - bytePosition);
      const start = bytePosition;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);

      // Update byte position for next chunk
      bytePosition = end;

      // Create form data for this chunk
      const chunkFormData = new FormData();
      chunkFormData.append('chunk', chunk, `chunk_${chunkIndex}`);

      try {
        // Record the start time to calculate upload speed
        const startTime = Date.now();

        // Upload the chunk with retry logic and CSRF token
        // Pass a custom progress callback that calculates overall progress
        const chunkResponse = await uploadChunkWithRetry(
          userId,
          sessionId,
          chunkIndex,
          chunkFormData,
          (chunkProgress) => {
            // Calculate overall progress based on bytes uploaded
            const bytesUploaded = bytePosition - chunk.size + (chunk.size * (chunkProgress / 100));
            const overallProgress = Math.round((bytesUploaded / file.size) * 100);
            onProgress(overallProgress);
          },
          csrfToken
        );

        // Calculate upload speed for this chunk
        const endTime = Date.now();
        const uploadTimeSec = (endTime - startTime) / 1000; // in seconds
        const chunkSizeInBytes = chunk.size; // Use actual chunk size
        const uploadSpeed = uploadTimeSec > 0 ? chunkSizeInBytes / uploadTimeSec : 0;

        // Adjust chunk size based on upload speed
        if (uploadTimeSec > 0) {
          adjustChunkSize(uploadSpeed);
        }

        // Check if this is the final chunk response which contains the videoId
        if (chunkResponse && chunkResponse.data && chunkResponse.data.videoId) {
          finalResponse = chunkResponse.data;
        }

        chunkIndex++;

        // Update progress based on bytes uploaded
        const overallProgress = Math.round((bytePosition / file.size) * 100);
        onProgress(overallProgress);
      } catch (error) {
        console.error(`Failed to upload resumed chunk ${chunkIndex}:`, error);

        // Handle rate limit errors specifically
        if (error.response && error.response.status === 429) {
          onError(`Rate limit exceeded. Please wait a few minutes and try again.`);
        } else {
          // Provide more specific error messages
          let errorMessage = error.message;
          if (error.response) {
            if (error.response.status === 400) {
              errorMessage = `Bad request for chunk ${chunkIndex}: ${error.response.data.message || 'Invalid chunk data'}`;
            } else if (error.response.status === 401) {
              errorMessage = 'Authentication failed. Please log in again.';
            } else if (error.response.status === 403) {
              errorMessage = `Access denied for chunk ${chunkIndex}. You do not have permission to upload this chunk.`;
            } else if (error.response.status === 404) {
              errorMessage = 'Upload session not found. The upload may have expired.';
            } else if (error.response.status === 500) {
              errorMessage = `Server error when uploading chunk ${chunkIndex}: ${error.response.data.message || 'Internal server error. Please try again.'}`;
            } else {
              errorMessage = `Failed to upload resumed chunk ${chunkIndex} (${error.response.status}): ${error.response.data.message || error.message}`;
            }
          }
          onError(errorMessage);
        }
        throw error;
      }
    }
    
    // Return the final response (which contains videoId) instead of trying to get session status
    if (finalResponse) {
      return finalResponse;
    } else {
      // Fallback: try to get session status (for cases where final chunk didn't return videoId)
      try {
        const finalStatusResponse = await videoAPI.getUploadSessionStatus(userId, sessionId);
        return finalStatusResponse.data;
      } catch (error) {
        // If we can't get session status, return a success object
        return { message: 'Upload completed successfully' };
      }
    }
  } catch (error) {
    // console.error('Resume upload failed:', error);
    onError(`Resume upload failed: ${error.message}`);
    throw error;
  }
};